'use client';

import { DrawerMenu, useAutoComplete } from '@/modules/navigation';
import {
	FaRegUser,
	FaUser,
} from 'react-icons/fa';
import {
	Box,
	HStack,
	IconButton,
	useBreakpointValue,
} from '@chakra-ui/react';
import {
	Link,
	NativeSelectField,
	NativeSelectRoot,
	NextImageWithSkeleton,
} from '@packages/chakra-v3';
import { useRouter } from 'next/navigation';
import { RiShoppingBag2Fill, RiShoppingBag2Line } from 'react-icons/ri';

import { APP_ROUTES } from '@/routes';
import type { Locale } from '@/libs/next-intl';
import React from 'react';
import { setUserLocale } from '@/utils/locale';
import { useLocale } from 'next-intl';
import type { NavigationMenuDocument } from '@/modules/sanity-studio/types';
import { Autocomplete } from '@/modules/shared';

type NavbarProps = {
	isAuth:         boolean;
	isEmpty: 	      boolean;
	onClickAccount: () => void;
	onClickCart:    () => void;
	navigationMenu: NavigationMenuDocument;
};

const Navbar = ({
	isAuth,
	isEmpty,
	onClickAccount,
	onClickCart,
	navigationMenu,
}: NavbarProps) => {
	const UserIcon = isAuth ? FaUser : FaRegUser;
	const logoSize = useBreakpointValue({
		base: {
			height: '20px',
			width:  '100px',
		},
		md: {
			height: '30px',
			width:  '150px',
		},
	}, {  fallback: 'base' }) || {
		height: '20px',
		width:  '100px',
	};
	const { height, width } = logoSize;
	const initialLocale = useLocale() as Locale;
	const router = useRouter();

	const autocomplete = useAutoComplete();
	const handleOnSelect = (collectionSlug: string, slug: string) => {
		const url = APP_ROUTES.SHOP.PRODUCT_DETAILS.replace(':collectionSlug', collectionSlug).replace(':productSlug', slug);
		router.push(url);
	};

	const handleChangeLocale = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const locale = e.target.value as Locale;
		setUserLocale(locale);
	};

	return (
		<React.Fragment>
			<HStack
				bg={'white'}
				justifyContent={'space-between'}
				position={'sticky'}
				px={{
					base: 4,
					md:   8,
				}}
				py={4}
				shadow={'2xl'}
				top={0}
				width={'100%'}
				zIndex={1000}
			>
				<HStack gap={8}>
					<DrawerMenu drawMenuItems={navigationMenu.menu} title={navigationMenu.title} />
					<Box hideBelow={'lg'}>
						<Autocomplete
							bg={'white'}
							hook={autocomplete as never}
							placeholder={'search product'}
							size={'xs'}
							w={'300px'}
							onSelectSuggestion={handleOnSelect}
						/>
					</Box>
				</HStack>
				<Link href={APP_ROUTES.HOME}>
					<NextImageWithSkeleton
						alt={'logo'}
						height={height}
						objectFit={'contain'}
						src={'/logo.png'}
						width={width}
					/>
				</Link>
				<HStack
					alignItems={'center'}
					gap={{
						base: 2,
						md:   4,
					}}
				>
					<IconButton
						aria-label={'Open menu'}
						p={0}
						variant={'ghost'}
						onClick={onClickAccount}
					>
						<UserIcon />
					</IconButton>
					<Box position={'relative'}>
						{!isEmpty && (
							<Box
								alignItems={'center'}
								bg={'green.500'}
								borderRadius={'full'}
								display={'flex'}
								height={'12px'}
								justifyContent={'center'}
								position={'absolute'}
								right={0}
								top={0}
								width={'12px'}
							/>
						)}
						<IconButton
							aria-label={'Open menu'}
							p={0}
							variant={'ghost'}
							onClick={onClickCart}
						>
							{isEmpty ? <RiShoppingBag2Line /> : <RiShoppingBag2Fill />}
						</IconButton>
					</Box>
					<NativeSelectRoot hideBelow={'md'} pl={2} size={'sm'}>
						<NativeSelectField defaultValue={initialLocale} onChange={handleChangeLocale}>
							<option value={'en'}>EN</option>
							<option value={'th'}>TH</option>
						</NativeSelectField>
					</NativeSelectRoot>
				</HStack>
			</HStack>
		</React.Fragment>
	);
};

export default Navbar;